import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/api-client';

export interface PartyChatMessage {
    id: number;
    username: string;
    message: string;
    created_at: string;
    is_streamer?: boolean;
    original_lang?: string;  // Language of the original message ('ja' | 'en')
    display_text?: string | null;  // Translated text if available
}

interface PartyCountUpdate {
    type: 'party_count_update';
    party_count: number;
    viewers: Array<{ username: string; joined_at: string }>;
}

interface ChatMessageDeleted {
    type: 'chat_message_deleted';
    id: number;
}

type PartyWebSocketMessage = PartyChatMessage | PartyCountUpdate | ChatMessageDeleted;

/** Apply a websocket event to the message list; deletions remove the message (requirement 6.4). */
export function applyPartyChatEvent(prev: PartyChatMessage[], data: PartyWebSocketMessage): PartyChatMessage[] {
    if ('type' in data && data.type === 'chat_message_deleted') {
        return prev.filter((m) => m.id !== data.id);
    }
    if ('type' in data) return prev;
    const message = data as PartyChatMessage;
    if (prev.some((m) => m.id === message.id)) return prev;
    return [...prev, message];
}

interface UsePartyChatReturn {
    messages: PartyChatMessage[];
    sendMessage: (message: string) => void;
    isConnected: boolean;
    isLoading: boolean;
    error: Error | null;
    partyCountFromWs: number | null;
}

/**
 * Party chat WebSocket connection hook
 * Manages WebSocket connection for party chat
 * Loads historical messages on connect and receives new messages via WebSocket
 * @param scheduleId - Event schedule ID to filter chat history by schedule
 */
export function usePartyChat(
    streamKey: string | null,
    viewerUsername: string | null,
    isEnabled: boolean,
    scheduleId?: string // UUID形式
): UsePartyChatReturn {
    const { i18n } = useTranslation();
    const [messages, setMessages] = useState<PartyChatMessage[]>([]);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [partyCountFromWs, setPartyCountFromWs] = useState<number | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const historyLoadedRef = useRef<boolean>(false);

    // Get user's preferred language for translation
    const userLang = i18n.language === 'en' ? 'en' : 'ja';

    // Load chat history when enabled
    useEffect(() => {
        if (!isEnabled || !streamKey) {
            historyLoadedRef.current = false;
            return;
        }

        // Only load history once per stream
        if (historyLoadedRef.current) {
            return;
        }

        const loadHistory = async () => {
            try {
                console.log('[PartyChat] Loading chat history for stream:', streamKey, 'scheduleId:', scheduleId, 'lang:', userLang);
                const history = await apiClient.getChatMessages(streamKey, 100, scheduleId, userLang);

                // Convert ChatMessage to PartyChatMessage format
                const historicalMessages: PartyChatMessage[] = history.map(msg => ({
                    id: msg.id,
                    username: msg.username,
                    message: msg.message,
                    created_at: msg.created_at,
                    is_streamer: msg.is_streamer,
                    original_lang: msg.original_lang,
                    display_text: msg.display_text,
                }));

                setMessages(historicalMessages);
                historyLoadedRef.current = true;
                console.log('[PartyChat] Loaded', historicalMessages.length, 'historical messages');
            } catch (err) {
                console.error('[PartyChat] Failed to load chat history:', err);
                // Don't set error - continue with WebSocket for new messages
            }
        };

        loadHistory();

        // Cleanup: reset history when stream changes or disabled
        return () => {
            historyLoadedRef.current = false;
            setMessages([]);
        };
    }, [isEnabled, streamKey, scheduleId, userLang]);

    useEffect(() => {
        // Only connect if enabled and required params are present
        if (!isEnabled || !streamKey || !viewerUsername) {
            setIsConnected(false);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const initWebSocket = async () => {
            try {
                // Get authentication token
                const token = apiClient.getAccessTokenForWebSocket();
                if (!token) {
                    throw new Error('No authentication token available');
                }

                // Construct WebSocket URL (no token in URL for security)
                // Use VITE_WS_BACKEND_URL for WebSocket (bypasses Vite proxy issues)
                // Fall back to VITE_BACKEND_URL or same host
                const wsBackendUrl = import.meta.env.VITE_WS_BACKEND_URL;
                const backendUrl = import.meta.env.VITE_BACKEND_URL;
                const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

                let wsUrl: string;
                if (wsBackendUrl) {
                    // Direct WebSocket connection to backend
                    const url = new URL(wsBackendUrl);
                    // Handle both https: and wss: protocols
                    const wsProtocol = (url.protocol === 'https:' || url.protocol === 'wss:') ? 'wss:' : 'ws:';
                    wsUrl = `${wsProtocol}//${url.host}/api/chat/ws/${streamKey}`;
                } else if (backendUrl) {
                    const url = new URL(backendUrl);
                    const wsProtocol = (url.protocol === 'https:' || url.protocol === 'wss:') ? 'wss:' : 'ws:';
                    wsUrl = `${wsProtocol}//${url.host}/api/chat/ws/${streamKey}`;
                } else {
                    wsUrl = `${protocol}//${window.location.host}/api/chat/ws/${streamKey}`;
                }

                console.log('[PartyChat] Connecting to:', wsUrl);

                const ws = new WebSocket(wsUrl);

                ws.onopen = () => {
                    console.log('[PartyChat] WebSocket connected, sending auth message');
                    // Send authentication message immediately after connection
                    ws.send(JSON.stringify({ type: 'auth', token }));
                    setIsConnected(true);
                    setIsLoading(false);
                    setError(null);
                };

                ws.onmessage = (event) => {
                    try {
                        const data: PartyWebSocketMessage = JSON.parse(event.data);

                        // Check if it's a party count update
                        if ('type' in data && data.type === 'party_count_update') {
                            setPartyCountFromWs(data.party_count);
                            console.log('[PartyChat] Party count updated:', data.party_count);
                            return;
                        }

                        // A chat message, or the removal of one by staff
                        setMessages((prev) => applyPartyChatEvent(prev, data));
                    } catch (err) {
                        console.error('[PartyChat] Failed to parse message:', err);
                    }
                };

                ws.onerror = (event) => {
                    console.error('[PartyChat] WebSocket error:', event);
                    setError(new Error('WebSocket connection error'));
                    setIsLoading(false);
                };

                ws.onclose = () => {
                    console.log('[PartyChat] WebSocket disconnected');
                    setIsConnected(false);
                    setIsLoading(false);
                };

                wsRef.current = ws;
            } catch (err) {
                console.error('[PartyChat] Failed to initialize WebSocket:', err);
                setError(err instanceof Error ? err : new Error('Failed to connect'));
                setIsLoading(false);
            }
        };

        initWebSocket();

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [streamKey, viewerUsername, isEnabled]);

    const sendMessage = useCallback(
        async (message: string) => {
            if (!streamKey) {
                console.error('[PartyChat] Cannot send message: No stream key');
                return;
            }

            try {
                // Send message via HTTP POST with original language
                // Backend will broadcast via WebSocket
                await apiClient.sendChatMessage(streamKey, message, scheduleId, userLang);
            } catch (err) {
                console.error('[PartyChat] Failed to send message:', err);
                throw err;
            }
        },
        [streamKey, scheduleId, userLang]
    );

    return {
        messages,
        sendMessage,
        isConnected,
        isLoading,
        error,
        partyCountFromWs,
    };
}
